import React, { useState, useEffect, useMemo } from "react";
import { toast } from "react-toastify";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  CircularProgress,
  Typography,
  InputBase,
  InputAdornment,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import SearchIcon from "@material-ui/icons/Search";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import ButtonWithSpinner from "../ButtonWithSpinner";

const useStyles = makeStyles((theme) => ({
  dialogContent: {
    minHeight: "300px",
    padding: theme.spacing(2),
  },
  listItem: {
    borderBottom: "1px solid #eee",
  },
  sendButton: {
    backgroundColor: theme.palette.primary.main,
    color: "#fff",
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  searchWrapper: {
    display: "flex",
    alignItems: "center",
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,0.08)"
        : "rgba(0,0,0,0.05)",
    borderRadius: "50px",
    padding: "6px 16px",
    marginBottom: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    transition: "border-color 0.2s ease, box-shadow 0.2s ease",
    "&:focus-within": {
      borderColor: theme.palette.primary.main,
      boxShadow: `0 0 0 2px ${theme.palette.primary.main}22`,
    },
  },
  searchIcon: {
    color: theme.palette.text.secondary,
    marginRight: theme.spacing(1),
    fontSize: 20,
  },
  searchInput: {
    flex: 1,
    fontSize: "0.9rem",
    "& input": {
      padding: 0,
    },
  },
  emptySearch: {
    textAlign: "center",
    marginTop: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
}));

const FlowBuilderChatModal = ({ open, onClose, ticket }) => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [sendingFlowId, setSendingFlowId] = useState(null);
  const [flows, setFlows] = useState([]);
  const [searchParam, setSearchParam] = useState("");

  useEffect(() => {
    if (open) {
      loadFlows();
      setSearchParam("");
    }
  }, [open]);

  const loadFlows = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/flowbuilder");
      setFlows(data.flows || []);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const filteredFlows = useMemo(() => {
    if (!searchParam.trim()) return flows;
    const lower = searchParam.toLowerCase().trim();
    return flows.filter((flow) =>
      flow.name?.toLowerCase().includes(lower)
    );
  }, [flows, searchParam]);

  const handleSendFlow = async (flow) => {
    if (!ticket || !ticket.id) return;

    setSendingFlowId(flow.id);
    try {
      await api.post(`/tickets/${ticket.id}/flow`, {
        flowId: flow.id,
      });
      toast.success("Fluxo disparado com sucesso!");
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setSendingFlowId(null);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      scroll="paper"
    >
      <DialogTitle id="form-dialog-title">
        Selecione o Fluxo a Ser Disparado
      </DialogTitle>

      <DialogContent dividers className={classes.dialogContent}>
        {/* Barra de pesquisa estilo pílula */}
        <div className={classes.searchWrapper}>
          <SearchIcon className={classes.searchIcon} />
          <InputBase
            className={classes.searchInput}
            placeholder="Pesquisar fluxo..."
            value={searchParam}
            onChange={(e) => setSearchParam(e.target.value)}
            autoFocus={false}
          />
        </div>

        {loading ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "200px",
            }}
          >
            <CircularProgress />
          </div>
        ) : filteredFlows.length === 0 ? (
          <Typography className={classes.emptySearch}>
            {flows.length === 0
              ? "Nenhum fluxo encontrado."
              : `Nenhum fluxo corresponde a "${searchParam}".`}
          </Typography>
        ) : (
          <List>
            {filteredFlows.map((flow) => (
              <ListItem key={flow.id} className={classes.listItem}>
                <ListItemText
                  primary={flow.name}
                  secondary={flow.active ? "Ativo" : "Desativado"}
                />
                <ListItemSecondaryAction>
                  <ButtonWithSpinner
                    variant="contained"
                    size="small"
                    className={classes.sendButton}
                    loading={sendingFlowId === flow.id}
                    disabled={sendingFlowId !== null || !flow.active}
                    onClick={() => handleSendFlow(flow)}
                  >
                    Enviar
                  </ButtonWithSpinner>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="secondary" variant="outlined">
          {i18n.t("transferTicketModal.buttons.cancel")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FlowBuilderChatModal;
